/**
 * Header nav disclosure dropdowns.
 *
 *   <ul>                   level-1 list — receives `has-menu-open` while a
 *                          section *other than* the active one is user-opened
 *     [data-menu]          parent <li> — receives `menu-is-active` when shown
 *       [data-menu-trigger]  button — toggles the disclosure
 *       [data-menu-content]  submenu <ul>
 *
 * Only one submenu is shown at a time, but the two breakpoints disagree on what
 * "nothing open" means — the same markup is a horizontal bar on desktop and a
 * full-screen accordion inside the menu overlay on mobile:
 *
 *   desktop (≥1440px) — the section active on page load (its trigger carries
 *     `is-initialy-active`) is a resting state: its second-level nav shows
 *     whenever the user hasn't opened another one. Opening another replaces it;
 *     closing — via the trigger, Escape, or an outside click — falls back to it.
 *     The resting section can never be left collapsed; that persistent second
 *     row is the point.
 *   mobile (<1440px) — a plain accordion with no resting fallback. Every
 *     section, including the current page's, collapses on a second click, and
 *     closing one leaves nothing open instead of springing back to the current
 *     page's section. The current section merely starts expanded.
 *
 * A single render() is the source of truth, driven by `userOpen` (see
 * shownEntry() for how the two models differ). The resting section's
 * `menu-is-active` is never destroyed (the old approach stripped it on every
 * close and tried to restore it, which lost the active child).
 */

const OPEN_CLASS = 'menu-is-active';
const PARENT_OPEN_CLASS = 'has-menu-open';
const menus = [];
const desktop = matchMedia('(min-width: 1440px)'); // same query as headerScroll.js
let defaultEntry = null; // section active on page load — desktop's resting fallback
let userOpen = null; // section the user explicitly opened, or null
let idCounter = 0;

function nextId(prefix) {
    idCounter += 1;
    return prefix + '-' + idCounter;
}

// The section whose submenu is on screen: `userOpen` if set, otherwise desktop's
// resting section — on mobile there is no fallback, so nothing is shown.
function shownEntry() {
    return userOpen || (desktop.matches ? defaultEntry : null);
}

// has-menu-open de-emphasises the active section's button, but only while a
// *different* section is open.
function render() {
    const shown = shownEntry();
    menus.forEach((m) => {
        const isShown = m === shown;
        m.root.classList.toggle(OPEN_CLASS, isShown);
        m.trigger.setAttribute('aria-expanded', String(isShown));
        m.content.hidden = !isShown;
    });

    const lists = new Set(menus.map((m) => m.root.parentElement).filter(Boolean));
    lists.forEach((list) => {
        const deEmphasise = Boolean(userOpen) && userOpen !== defaultEntry
            && userOpen.root.parentElement === list;
        list.classList.toggle(PARENT_OPEN_CLASS, deEmphasise);
    });
}

// Set the user-opened section (null → desktop: resting state, mobile: all shut).
// On desktop, opening the active section is just a return to rest, so it can
// never be left collapsed; on mobile it's an ordinary accordion section.
function setOpen(entry) {
    userOpen = desktop.matches && entry === defaultEntry ? null : entry;
    render();
}

function toggle(entry) {
    setOpen(shownEntry() === entry ? null : entry);
}

function init() {
    document.querySelectorAll('[data-menu]').forEach((root) => {
        const trigger = root.querySelector('[data-menu-trigger]');
        const content = root.querySelector('[data-menu-content]');
        if (!trigger || !content) return;

        if (!trigger.id) trigger.id = nextId('menu-trigger');
        if (!content.id) content.id = nextId('menu-content');

        trigger.setAttribute('aria-controls', content.id);
        content.setAttribute('aria-labelledby', trigger.id);

        const entry = { root, trigger, content };
        menus.push(entry);
        if (trigger.classList.contains('is-initialy-active')) defaultEntry = entry;

        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            toggle(entry);
        });
    });

    if (menus.length === 0) return;

    // Mobile has no resting fallback, so the current page's section has to be
    // opened explicitly to start out expanded.
    if (!desktop.matches) userOpen = defaultEntry;

    render(); // normalise aria-expanded / hidden to the resting state

    // Carry the shown section across a breakpoint change, in both directions:
    // what desktop shows implicitly at rest, mobile has to hold in `userOpen` —
    // and vice versa, so a section left open on mobile doesn't count as
    // "user-opened" on desktop and de-emphasise its own button (has-menu-open).
    desktop.addEventListener('change', () => {
        if (desktop.matches) {
            if (userOpen === defaultEntry) userOpen = null;
        } else if (!userOpen) {
            userOpen = defaultEntry;
        }
        render();
    });

    // Escape and outside-click dismiss the *desktop* dropdown, which is absolutely
    // positioned over the page. On mobile the nav is a full-screen overlay that
    // owns both gestures itself (overlay.js closes it on Escape, and every tap
    // lands inside the menu — dismissing there would collapse the section the user
    // is reading), so sections are toggled by their triggers alone.
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (!desktop.matches) return;
        if (!userOpen) return;
        const trigger = userOpen.trigger;
        setOpen(null);
        trigger.focus();
    });

    document.addEventListener('click', (e) => {
        if (!desktop.matches) return;
        if (!userOpen) return;
        if (userOpen.root.contains(e.target)) return;
        setOpen(null);
    });

    // The compact header slides the nav (and its absolutely-positioned submenu)
    // off-screen; close any user-opened dropdown so it doesn't linger behind the
    // compact button. The resting section is untouched — it returns with the nav
    // on expand, so nothing needs restoring.
    window.addEventListener('header:compact', (e) => {
        if (e.detail && e.detail.compact) setOpen(null);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
